'use client';

import { useEffect } from 'react';
import { useParams } from 'next/navigation';
import { newrelic } from '@hen-site/lib/newrelic/client';
import { logger } from '@hen-site/lib/logger/client';

/**
 * Set the tenantId as a custom attribute in New Relic browser agent.
 * This component must be rendered inside the [tenant] route segment
 * to have access to the tenant param via useParams.
 * @returns null
 */
export const TenantClientReady = () => {
  const params = useParams();
  const tenantId = params?.tenant as string | undefined;

  useEffect(() => {
    if (tenantId) {
      let retryCount = 0;
      const maxRetries = 50; // 50 * 100ms = 5 seconds max wait time

      // Wait for New Relic to be available before setting attributes
      const checkNewRelic = () => {
        if (typeof window !== 'undefined' && window.newrelic) {
          logger.info(
            { tenantId },
            'Setting tenantId in New Relic browser agent',
          );
          newrelic.setCustomAttribute('tenantId', tenantId);
        } else if (retryCount < maxRetries) {
          // Retry after 100ms if NR not yet available and we haven't exceeded max retries
          retryCount++;
          setTimeout(checkNewRelic, 100);
        } else {
          logger.warn(
            'TenantClientReady - New Relic not available after 5 seconds, giving up',
          );
        }
      };

      checkNewRelic();
    }
  }, [tenantId]);

  return null;
};
